import React, { useState, useEffect, useRef, useMemo,useCallback } from 'react';
import { styled } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import { 
    Tooltip, 
    Switch, 
    Select, 
    MenuItem, 
    Button, 
    Typography, 
    Snackbar,
    useMediaQuery,
    Box
} from '@mui/material';
import { 
    ToolbarButton,
    DataGridPro, 
    Toolbar, 
    ColumnsPanelTrigger, 
    FilterPanelTrigger, 
    QuickFilter,
    QuickFilterControl,
    QuickFilterClear,
    QuickFilterTrigger,
} from '@mui/x-data-grid-pro';
import { createServiceBusClient, createMessageReceiverAsync, createMessageSender } from "../modules/createServiceBusClient"
import { omit, uniqBy, endsWith } from "lodash";
import { SaveAlt, Replay, ViewColumn, FilterList, Search, Cancel } from '@mui/icons-material';
import { saveAs } from 'file-saver';
import { Buffer } from "buffer";
import flatten from 'flat';
import { createEvent, createErrorEvent, createMessageEvent } from "../modules/logManager";
import Badge from '@mui/material/Badge';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Menu from '@mui/material/Menu';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import CheckIcon from '@mui/icons-material/Check';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import { alpha } from "@mui/material/styles";

const RootContainer = styled('div')({
    width: '100%',
    position: 'relative'
});

const GridContainer = styled('div')({
    width: '100%',
    height: '600px',
});

const GridStyled = styled(DataGridPro)(({ theme }) => ({
    '&.MuiDataGrid-root .MuiDataGrid-cell:focus': {
        outline: 'none',
    },
    '&&.MuiDataGrid-root .MuiDataGrid-columnHeaderTitleContainer .MuiDataGrid-iconButtonContainer .MuiIconButton-root': {
        color: theme.palette.primary.main
    }
}));

const CellText = styled('span')({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word'
});

/* 
const FloatingToolbar = styled('div')({
    zIndex: 1,
    marginTop: "3px",
    marginLeft: "3px",
    minWidth: "250px",
    position: 'absolute',
});
*/

const StyledTextField = styled(TextField)(({ theme, ownerState = { expanded: true } }) => ({
    gridArea: '1 / 1',
    overflowX: 'clip',
    width: ownerState.expanded ? 260 : 'var(--trigger-width)',
    opacity: ownerState.expanded ? 1 : 0,
    transition: theme.transitions.create(['width', 'opacity']),
  }));

const StyledToolbarButton = styled(ToolbarButton)(({ theme, ownerState = { expanded: true } }) => ({
    gridArea: '1 / 1',
    width: 'min-content',
    height: 'min-content',
    zIndex: 1,
    opacity: ownerState.expanded ? 0 : 1,
    pointerEvents: ownerState.expanded ? 'none' : 'auto',
    transition: theme.transitions.create(['opacity']),
}));

const StyledQuickFilter = styled(QuickFilter)({
    display: 'grid',
    alignItems: 'center',
  });


let isFileSaverSupported = false;
try {
    isFileSaverSupported = !!new Blob();
} catch {}

export default function BrowseMessages({ entityInfo, credentials, selectedEntity, setErrorMessage, onMessageSelected, isLicenced, isDisplayed, onMessageEventReceived }) {
    const [fromDLQ, setFromDLQ] = useState(false);
    const peekBatchCount = 250;
    const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));
    const isNotLargeScreen = useMediaQuery(theme => theme.breakpoints.down('lg'));
    const minColumnWidth = isSmallScreen ? 30 : isNotLargeScreen ? 50 : 100;
    const getBodyAsText = (value) => {
        return value && Buffer.isBuffer(value) ? value.toString('utf8') : typeof(value) === "object" ? JSON.stringify(value) : value && value.toString ? value.toString() : value;
    }
    
    const renderCell = useCallback((params) => {
        const value = params.field === "body" ? params.value || "[Empty]" :
        params.field === "subject" ? params.value || "[Empty]" :
        endsWith(params.field, "Utc") && params.value && params.value.toLocaleString ? params.value.toLocaleString() :
        params.field.indexOf("Body.") === 0 && Array.isArray(params.value) ? getBodyAsText(params.value) :
        params.value && params.value.toString ? params.value.toString() : params.value;
        return <Tooltip placement='auto-start' enterDelay={500} enterNextDelay={500} leaveDelay={0} title={ value && value.length >= 300 ? value.substring(0, 300) + "..." : value } >
            <CellText>{ value }</CellText>
        </Tooltip>
    }, []);
    const baseColumns = useMemo(() => [
        { field: 'subject', type: 'string', headerName: 'Subject', flex: 1, renderCell, minWidth: minColumnWidth },
        { field: 'body', type: 'string', headerName: 'Body', flex: 1, renderCell, minWidth: minColumnWidth },
        { field: 'enqueuedTimeUtc', type: 'dateTime', headerName: 'Enqueued Time', flex: 1, renderCell, minWidth: minColumnWidth },
        { field: 'messageId', type: 'string', headerName: 'Message Id', flex: 1, renderCell, minWidth: minColumnWidth },
        { field: 'lockToken', type: 'string', headerName: 'Lock Token', flex: 1, renderCell, minWidth: minColumnWidth },
        { field: 'deadLetterSource', type: 'string', headerName: 'Dead Letter Source', flex: 1, renderCell, minWidth: minColumnWidth },
        { field: 'expiresAtUtc', type: 'dateTime', headerName: 'Expires At', flex: 1, renderCell, minWidth: minColumnWidth },
        { field: 'lockedUntilUtc', type: 'dateTime', headerName: 'Locked Until', flex: 1, renderCell, minWidth: minColumnWidth },
        { field: 'enqueuedSequenceNumber', type: 'number', headerName: 'Enqueued Sequence Number', flex: 1, renderCell, minWidth: minColumnWidth },
        { field: 'sequenceNumber', type: 'number', headerName: 'Sequence Number', flex: 1, renderCell, minWidth: minColumnWidth },
        { field: 'contentType', type: 'string', headerName: 'Content Type', flex: 1, renderCell, minWidth: minColumnWidth },
        { field: 'correlationId', type: 'string', headerName: 'Correlation Id', flex: 1, renderCell, minWidth: minColumnWidth },
        { field: 'sessionId', type: 'string', headerName: 'Session Id', flex: 1, renderCell, minWidth: minColumnWidth },
        { field: 'replyToSessionId', type: 'string', headerName: 'Reply To Session Id', flex: 1, renderCell, minWidth: minColumnWidth },
        { field: 'timeToLive', type: 'number', headerName: 'Time To Live', flex: 1, renderCell, minWidth: minColumnWidth },
        { field: 'to', type: 'string', headerName: 'To', flex: 1, renderCell, minWidth: minColumnWidth },
        { field: 'replyTo', type: 'string', headerName: 'Reply To', flex: 1, renderCell, minWidth: minColumnWidth },
        { field: 'scheduledEnqueueTimeUtc', type: 'dateTime', headerName: 'Scheduled Enqueue At', flex: 1, renderCell, minWidth: minColumnWidth },
        { field: 'id', type: 'string', headerName: 'Grid item Id', flex: 1, renderCell, minWidth: minColumnWidth },
        { field: 'deliveryCount', type: 'number', headerName: 'Delivery Count', flex: 1, renderCell, minWidth: minColumnWidth },
        { field: 'deadLetterReason', type: 'string', headerName: 'Dead Letter Reason', flex: 1, renderCell, minWidth: minColumnWidth },
        { field: 'deadLetterErrorDescription',  type: 'string',  headerName: 'Dead Letter Error Description',  flex: 1, renderCell, minWidth: minColumnWidth },
    ], [minColumnWidth, renderCell]);

    const [allRows, setAllRows] = useState([]);
    const [columns, setColumns] = useState(baseColumns);
    const [loading, setLoading] = React.useState(false);
    const [receiver, setReceiver] = React.useState(undefined);
    const [maxMessagesToLoad, setMaxMessagesToLoad] = React.useState("1000");
    const [loadedAllMessages, setLoadedAllMessages] = React.useState(false);
    const [clickedRowId, setClickedRowId] = React.useState(null);
    const DENSITY_OPTIONS = [
        { label: 'Compact', value: 'compact' },
        { label: 'Standard', value: 'standard' },
        { label: 'Comfortable', value: 'comfortable' },
      ];
    const [density, setDensity] = React.useState(isSmallScreen ? 'compact' : 'standard');
    const [rowSelectionModel, setRowSelectionModel] = useState({
        type: 'include',
        ids: new Set(),
    });
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({
        subject: false,
        lockToken: false,
        deadLetterSource: false,
        expiresAtUtc: false,
        lockedUntilUtc: false,
        enqueuedSequenceNumber: false,
        contentType: false,
        correlationId: false,
        sessionId: false,
        replyToSessionId: false,
        timeToLive: false,
        to: false,
        replyTo: false,
        scheduledEnqueueTimeUtc: false,
        deliveryCount: false,
        sequenceNumber: false,
        id: false,
        body: true,
        messageId: true,
        enqueuedTimeUtc: true,
        deadLetterReason: fromDLQ,
        deadLetterErrorDescription: fromDLQ
    });
    const [openSentMessagesNotification, setOpenSentMessagesNotification] = useState(false);
    const flattenOptions = { safe: true };
    const currentReceiver = useRef();
    currentReceiver.current = receiver;

    const prefixObjectProperties = (obj, prefix) => {
        let newObj = {};
        Object.keys(obj).forEach((key) => {
            newObj[prefix + key] = obj[key];
        })
        return newObj;
    }

    const replaySelectedMessages = async () => {
        window.dataLayer && window.dataLayer.push({
            event: 'replay_selected_messages',
            number_of_messages: rowSelectionModel.type === 'include' ? rowSelectionModel.ids.size : 0,
        });
        setLoading(true);
        const segments = entityInfo.entityName.split("/");
        let topicName;
        if (segments.length === 2) { 
          topicName = segments[0];
        } else {
          topicName = segments.slice(0, segments.length - 1).join("/");
        }
        const entityInfoToUse = entityInfo.entityType === "subscription" ? {
            ...entityInfo,
            entityType: "topic",
            entityName: topicName
        } : entityInfo; 
        const serviceBusClient = createServiceBusClient(entityInfoToUse, credentials, 3);
        const sender = createMessageSender(serviceBusClient, entityInfoToUse);
        let errored = false;
        for (const selectedRowId of rowSelectionModel.type === 'include' ? rowSelectionModel.ids : []) {
            const message = allRows.filter(row => row.id === selectedRowId)[0];
            const messageToSend = {
                ...omit(message, ["deadLetterReason", "deadLetterErrorDescription", "expiresAtUtc", "enqueuedTimeUtc", "lockedUntilUtc", "enqueuedSequenceNumber", "enqueuedTimeUtc"]),
                applicationProperties : omit(message.applicationProperties, ["DeadLetterReason", "DeadLetterErrorDescription"])
            }
            const event = Object.assign(createEvent(entityInfoToUse, "send"), {
                messageToSend
            });
            try {
                await sender.sendMessages(messageToSend);
                onMessageEventReceived(createMessageEvent(event, messageToSend));
            } catch (error) {
                setErrorMessage("Error replaying a message, check logs for details. Aborting sending rest of messages");
                onMessageEventReceived(createErrorEvent(event, entityInfoToUse, error, { messageToSend }, selectedEntity.type === "subscription" ? selectedEntity.topic : selectedEntity));
                errored = true;
                break;
            }
            if (selectedEntity && selectedEntity.refresh) {
                selectedEntity.refresh();
            }
        }
        if(!errored){
            setOpenSentMessagesNotification(true);
        }
        await sender.close();
        await serviceBusClient.close();
        setLoading(false);
    }

    function CustomToolbar() {
        const densityMenuTriggerRef = React.useRef(null);
        const [densityMenuOpen, setDensityMenuOpen] = React.useState(false);
        return (
          <Toolbar>
            <Box sx={{ flex: 1, mx: 0.5, minWidth: "235px" }}>
                <Button variant='text' size='small' onClick={() => setFromDLQ(!fromDLQ)}>
                    <Switch
                        size='small'
                        checked={fromDLQ}
                        onChange={(e) => setFromDLQ(e.target.checked)}
                        name="fromDLQ"
                        color="primary"
                    />
                    <Typography variant='button' fontSize={'small'} color='primary'>{isSmallScreen ? "DLQ" : "From DLQ"}</Typography>
                </Button>
                <Button variant='text' size='small'>
                    <Select
                        variant='standard'
                        color='primary'
                        size='small'
                        sx={{
                            color: 'primary.main',
                            fontSize: "small",
                            fontWeight: "500",
                        }}
                        labelId="limit-label"
                        label="Limit"
                        value={maxMessagesToLoad}
                        onChange={(e) => setMaxMessagesToLoad(e.target.value)}
                    >
                        <MenuItem value={50}>Limit: 50</MenuItem>
                        <MenuItem value={100}>Limit: 100</MenuItem>
                        <MenuItem value={250}>Limit: 250</MenuItem>
                        <MenuItem value={500}>Limit: 500</MenuItem>
                        <MenuItem value={750}>Limit: 750</MenuItem>
                        <MenuItem value={1000}>Limit: 1000</MenuItem>
                        <MenuItem disabled={!isLicenced} value={10000}>Limit: 10,000</MenuItem>
                        <MenuItem disabled={!isLicenced} value={50000}>Limit: 50,000</MenuItem>
                        <MenuItem disabled={!isLicenced} value={100000}>Limit: 100,000</MenuItem>
                        <MenuItem disabled={!isLicenced} value={0}>No limit</MenuItem>
                    </Select>
                </Button>
            </Box>
            <Tooltip title="Size">
                <ToolbarButton
                    color="primary"
                    ref={densityMenuTriggerRef}
                    id="density-menu-trigger"
                    aria-controls="density-menu"
                    aria-haspopup="true"
                    aria-expanded={densityMenuOpen ? 'true' : undefined}
                    onClick={() => setDensityMenuOpen(true)}
                >
                    <FormatSizeIcon fontSize="small" sx={{ ml: 'auto' }} />
                </ToolbarButton>
            </Tooltip>
            <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />
            <Menu
                id="density-menu"
                anchorEl={densityMenuTriggerRef.current}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={densityMenuOpen}
                onClose={() => setDensityMenuOpen(false)}
                slotProps={{
                list: {
                    'aria-labelledby': 'density-menu-trigger',
                },
                }}
            >
                {DENSITY_OPTIONS.map((option) => (
                <MenuItem
                    key={option.value}
                    onClick={() => {
                    setDensity(option.value);
                    setDensityMenuOpen(false);
                    }}
                >
                    <ListItemIcon>
                    {density === option.value && <CheckIcon fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText>{option.label}</ListItemText>
                </MenuItem>
                ))}
            </Menu>
           
            <Tooltip title="Filters">
                <FilterPanelTrigger
                render={(props, state) => (
                    <ToolbarButton color="primary" {...props} >
                        <Badge badgeContent={state.filterCount} color="primary" variant="dot">
                            <FilterList fontSize="small" />
                        </Badge>
                    </ToolbarButton>
                )}
                />
            </Tooltip>
            <Tooltip title="Columns">
                <span>
                <ColumnsPanelTrigger render={
                    (triggerProps, state) => (
                        <StyledToolbarButton
                            color="primary"
                            ownerState={{ expanded: state.expanded }}
                            // {...triggerProps} (passing all props causes flickering issue in popup so, instead, I am passing only tested props)
                            children={triggerProps.children}
                            theme={triggerProps.theme}
                            onClick={triggerProps.onClick}
                            className={triggerProps.className}
                            id={triggerProps.id}
                            data-mui-internal-clone-element={triggerProps['data-mui-internal-clone-element']}
                            aria-controls={triggerProps['aria-controls']}
                            aria-disabled={triggerProps['aria-disabled']}
                            aria-expanded={triggerProps['aria-expanded']}
                            aria-haspopup={triggerProps['aria-haspopup']}
                            onBlur={triggerProps.onBlur}
                            onFocus={triggerProps.onFocus}
                            onMouseLeave={triggerProps.onMouseLeave}
                            onMouseOver={triggerProps.onMouseOver}
                            onTouchEnd={triggerProps.onTouchEnd}
                            onTouchStart={triggerProps.onTouchStart}
                            // end of props added for fixing popup flickering issue instead of {...triggerProps}
                        />
                    )
                }>
                    <ViewColumn fontSize="small" />
                </ColumnsPanelTrigger>
                </span>
            </Tooltip>
            <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />
            {
                fromDLQ && (rowSelectionModel.type === 'include' ? rowSelectionModel.ids.size > 0 : false) ?
                <Tooltip title={`Resend ${rowSelectionModel.type === 'include' ? rowSelectionModel.ids.size : 0} selected messages to the ${entityInfo.entityType === "subscription" ? "parent topic" : entityInfo.entityType}`}>
                    <ToolbarButton color="primary" disabled={!isLicenced || loading} onClick={replaySelectedMessages}>
                        <Replay fontSize="small" />
                    </ToolbarButton>
                </Tooltip>
                : <></>
            }
            { 
                isFileSaverSupported ? 
                <Tooltip title={`Export to JSON`}>
                    <ToolbarButton color="primary" disabled={!isLicenced || loading} onClick={onSaveButnClick}>
                        <SaveAlt fontSize="small" />
                    </ToolbarButton>
                </Tooltip>
                : <></>
            }
            <StyledQuickFilter>
                <QuickFilterTrigger
                render={(triggerProps, state) => (
                    <Tooltip title="Search" enterDelay={0}>
                    <StyledToolbarButton
                        color="primary"
                        {...triggerProps}
                        ownerState={{ expanded: state.expanded }}
                        aria-disabled={state.expanded}
                    >
                        <Search fontSize="small" />
                    </StyledToolbarButton>
                    </Tooltip>
                )}
                />
                <QuickFilterControl
                render={({ ref, ...controlProps }, state) => (
                    <StyledTextField
                    {...controlProps}
                    ownerState={{ expanded: state.expanded }}
                    inputRef={ref}
                    aria-label="Search"
                    placeholder="Search..."
                    size="small"
                    slotProps={{
                        input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search fontSize="small" />
                            </InputAdornment>
                        ),
                        endAdornment: state.value ? (
                            <InputAdornment position="end">
                            <QuickFilterClear
                                edge="end"
                                size="small"
                                aria-label="Clear search"
                                material={{ sx: { marginRight: -0.75 } }}
                            >
                                <Cancel fontSize="small" />
                            </QuickFilterClear>
                            </InputAdornment>
                        ) : null,
                        ...controlProps.slotProps?.input,
                        },
                        ...controlProps.slotProps,
                    }}
                    />
                )}
                />
            </StyledQuickFilter>
          </Toolbar>
        );
    }

    const onColumnVisibilityChange = ({field, isVisible}) => {
        setColumns((columns) => columns.map((col) => {
            if (col.field === field) {
                col.hide = !isVisible;
            }
            return col;
        }));
    }

    const createData = ({ messageId, subject, body, _rawAmqpMessage, timeToLive, expiresAtUtc, sequenceNumber, ...rest }) => {
        return Object.assign({
            id: `${messageId}-${sequenceNumber.toNumber().toString()}`,
            messageId,
            subject: subject,
            body: body,
            timeToLive,
            sequenceNumber: sequenceNumber.toNumber(),
            ...rest
        }, timeToLive? {
            expiresAtUtc
        }: {});
    }

    useEffect(() => {
        setColumnVisibilityModel((existingModel) => {
            const newModel = {...existingModel};
            return {
                ...newModel,
                deadLetterReason: fromDLQ,
                deadLetterErrorDescription: fromDLQ
            };
        });
    }, [fromDLQ, isSmallScreen]);
    
    useEffect(() => {
        (async () => {
            if (isDisplayed && credentials && entityInfo && entityInfo.entityName && entityInfo.isValid && entityInfo.entityType !== "topic" && (!selectedEntity || !selectedEntity.forwardTo)
            ) {
                const client = createServiceBusClient(entityInfo, credentials, 3);
                const receiver = await createMessageReceiverAsync(client, entityInfo, Object.assign({}, 
                        { 
                            maxWaitTimeInMs: 3600000 // Message read timeout 60 minutes
                        },
                        fromDLQ ? { 
                            subQueueType: "deadLetter"
                        } : { }
                    ));
                setReceiver(receiver);
            } else {
                setReceiver(undefined);
            }
            onMessageSelected(null);
            setAllRows([]);
            setColumns(baseColumns);
            setLoadedAllMessages(false);
        })();
    }, [entityInfo, credentials, fromDLQ, onMessageSelected, selectedEntity, isDisplayed, baseColumns]);

    useEffect(() => {
        const getJsonColumns = (rows) => {
            const jsonColumns = [];
            const addedFields = new Set(columns.map(col => col.field));
            for (let i = 0; i < rows.length; i++) {
                const row = rows[i];
                if (row.body && typeof row.body === "object" && !Array.isArray(row.body) && !Buffer.isBuffer(row.body)) {
                    const flat = flatten(row.body, flattenOptions);
                    const keys = Object.keys(flat);
                    for (let j = 0; j < keys.length; j++) {
                        const key = keys[j];
                        const field = `Body.${key}`;
                        if (!addedFields.has(field)) {
                            jsonColumns.push({
                                field,
                                type: 'string',
                                headerName: field,
                                flex: 1,
                                renderCell,
                                minWidth: minColumnWidth,
                                hide: true
                            });
                        }
                    }
                }
            }
            return jsonColumns;
        }
        (async () => {
            if(receiver && (allRows.length < maxMessagesToLoad || maxMessagesToLoad === 0) && !loadedAllMessages) {
                setLoading(true);
                try {
                    const batchSize = maxMessagesToLoad === 0 || maxMessagesToLoad - allRows.length >= peekBatchCount 
                        ? peekBatchCount 
                        : maxMessagesToLoad - allRows.length;
                    const messages = await receiver.peekMessages(batchSize);
                    if(messages.length === 0) {
                        setLoadedAllMessages(true);
                    }
                    if (receiver === currentReceiver.current) {
                        const newRows = messages.map(createData);
                        const newColumns = getJsonColumns(newRows);
                        setColumns((existingColumns) => {
                            const mergedColumns = [...existingColumns, ...newColumns];
                            return mergedColumns.filter(
                                (column, index, self) =>
                                    index === self.findIndex((c) => c.field === column.field)
                            );
                        });
                        setColumnVisibilityModel((existingModel) => {
                            const newModel = {...existingModel};
                            newColumns.forEach(col => {
                                newModel[col.field] = false;
                            });
                            return newModel;
                        });
                        setAllRows((existingRows) => uniqBy([...existingRows, ...(newRows)], ({id}) => id));
                    } else if(messages.length > 0) {
                        setLoading(false);
                        window.dataLayer && window.dataLayer.push({
                            event: 'browsed_messages',
                            number_of_messages: allRows.length,
                            max_messages_to_load: maxMessagesToLoad,
                        });
                    }
                } catch (error) {
                    setLoading(false);
                    if (error.code !== "UnauthorizedAccess") {
                        window.appInsights.trackException({
                            exception: error,
                            properties: {
                                process: "browsing for messages"
                            }
                        });
                        console.error(error);
                        setErrorMessage(`Error while loading messages: ${error.message}`);
                    }
                }
            } else {
                setLoading(false);
                allRows.length > 0 && window.dataLayer && window.dataLayer.push({
                    event: 'browsed_messages',
                    number_of_messages: allRows.length,
                    max_messages_to_load: maxMessagesToLoad,
                });
            }


        })()
    // TODO: fix the dependencies and remove the eslint disable comment
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [receiver, allRows.length, maxMessagesToLoad, loadedAllMessages, setErrorMessage]);

    useEffect(() => {
        return () => {
            onMessageSelected(null);
        };
    }, [onMessageSelected]);

    const onRowClick = (params) => {
        setClickedRowId(params.id);
        const selectedMessage = omit(params.row, ["id", "sequenceNumber"]);
        selectedMessage.body = selectedMessage._body;
        onMessageSelected(omit(selectedMessage, ["_body", ...Object.keys(selectedMessage).filter(key => key.indexOf("Body.") === 0)]));
    }

    const onRowSelectionModelChange = (selectionModel) => {
        if(selectionModel.type === 'exclude' && selectionModel.ids.size === 0) {
            selectionModel = {
                type: 'include',
                ids: new Set(allRows.map(row => row.id))
            };
        }
        setRowSelectionModel(selectionModel);
        if(selectionModel.type === 'include' && selectionModel.ids.size === 0) onMessageSelected(null);
    }

    const onSaveButnClick = () => {
        try {
            const rowsToDownload = allRows.map((row) => omit(row, ["id"]));
            window.dataLayer && window.dataLayer.push({
                event: 'export_messages',
                number_of_messages: rowsToDownload.length,
            });
            var fileToSave = new Blob([JSON.stringify(rowsToDownload, null, 4)], {
                type: 'application/json'
            });
            saveAs(fileToSave, "messages.json");
        } catch (error) { 
            if (error instanceof RangeError) {
                setErrorMessage("Available memory exceeded. Please reduce the number of messages to export.");
            } else {
                throw error;
            }
        }
    };

    return (
        <RootContainer>
            <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} open={openSentMessagesNotification} autoHideDuration={6000} onClose={() => { setOpenSentMessagesNotification(false) }}>
                <Alert onClose={() => { setOpenSentMessagesNotification(false) }} severity="success" elevation={6} variant="filled">
                    Replayed {rowSelectionModel.type === 'include' ? rowSelectionModel.ids.size : 0} messages successfully
                </Alert>
            </Snackbar>
            <GridContainer>
                <GridStyled
                    sx={{
                        // base highlight
                        "& .MuiDataGrid-row.MuiDataGrid-row--clicked": (theme) => ({
                        backgroundColor: alpha(theme.palette.grey[500], 0.16),
                        }),
                        // keep hover a tad darker
                        "& .MuiDataGrid-row.MuiDataGrid-row--clicked:hover": (theme) => ({
                        backgroundColor: alpha(theme.palette.grey[500], 0.22),
                        }),
                    }}
                    density={density}
                    localeText={{
                        noRowsLabel: selectedEntity ? "No messages. Make sure you have read permissions on selected entity": "Select queue or subscription to load messages" 
                    }}
                    rows={allRows.map(row => Object.assign({}, row, {
                        _body: row.body,
                        body: getBodyAsText(row.body),
                        ...(row.body && !Buffer.isBuffer(row.body) && typeof row.body === 'object' && !Array.isArray(row.body) ? prefixObjectProperties(flatten(row.body, flattenOptions), "Body.") : {}),
                    }))}
                    slots={{
                        toolbar: CustomToolbar,
                    }}
                    getRowClassName={(params) =>
                        params.id === clickedRowId ? "MuiDataGrid-row--clicked" : ""
                    }
                    showToolbar={true}
                    columns={columns}
                    paginationMode='client'
                    loading={loading}
                    filterMode='client'
                    sortingMode='client'
                    onRowClick={onRowClick}
                    checkboxSelection={fromDLQ} // only action on selected rows is replaying messages from DLQ to parent entity, when more actions is added, this needs to be changed
                    disableRowSelectionOnClick={true}
                    onColumnVisibilityChange={onColumnVisibilityChange}
                    onRowSelectionModelChange={onRowSelectionModelChange}
                    rowSelectionModel={rowSelectionModel}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={setColumnVisibilityModel}
                />
            </GridContainer>
        </RootContainer>
    );
}

export { BrowseMessages };